<template>
  <section class="">
    <div
      class="px-4 mx-auto max-w-6xl lg:px-8"
      :class="[
        paddingTop === 'reduced' ? 'pt-6 lg:pt-6' : 'pt-12 lg:pt-16',
        paddingBottom === 'reduced' ? 'pb-6 lg:pb-6' : 'pb-12 lg:pb-16'
      ]"
    >
      <div
        class="px-6 py-10 mx-auto rounded-2xl bg-lifeboost-blue-light sm:px-10 sm:py-12 lg:px-14 lg:py-14"
      >
        <div class="flex flex-col gap-9 items-center">
          <div class="py-1 w-full text-center">
            <p
              v-if="brandLine"
              class="tracking-tight text-32 text-lifeboost-red sm:text-45 sm:leading-52 font-ww-bold"
              v-html="brandLine"
            />
            <h2
              v-if="title"
              class="leading-none text-black text-40 sm:text-51 font-ww-bold"
              v-html="title"
            />
          </div>

          <div class="flex flex-col gap-11 items-center w-full">
            <div class="flex flex-col gap-6 items-center w-full">
              <div
                v-if="formulaContent"
                class="px-6 py-6 w-full rounded-2xl border border-[#BDDCB5] bg-[#E8F0E6]"
              >
                <div
                  class="text-22 leading-normal tracking-tight text-center font-ww-bold [&_p:first-child]:text-lifeboost-ambassador [&_p:nth-child(2)]:text-lifeboost-ambassador [&_p:last-child]:text-lifeboost-red [&_p+p]:mt-0"
                  v-html="formulaContent"
                />
              </div>

              <div
                v-if="introContent"
                class="w-full text-17 leading-6 text-left text-[#4A4A4A] font-ww-regular [&_p+p]:mt-5"
                v-html="introContent"
              />
            </div>

            <div class="grid grid-cols-1 gap-6 w-full sm:grid-cols-2 lg:grid-cols-4">
              <article
                v-for="(product, index) in products"
                :key="index"
                class="flex flex-col gap-6 items-start"
              >
                <div class="w-full lg:h-60">
                  <nuxt-img
                    v-if="product.imageSrc"
                    :src="product.imageSrc"
                    :alt="product.imageAlt"
                    width="226"
                    height="248"
                    format="webp"
                    loading="lazy"
                    sizes="xs:100vw sm:316px lg:210px"
                    class="object-cover w-full rounded-xl lg:h-60"
                  />
                  <div
                    v-else
                    class="w-full h-60 rounded-xl bg-[#2A2A2A]"
                    aria-hidden="true"
                  />
                </div>

                <div class="flex flex-col gap-1.5 w-full text-center">
                  <a
                    v-if="product.href"
                    :href="product.href"
                    class="text-2xl leading-27 text-[#4A4A4A] font-ww-bold hover:opacity-80"
                    target="_blank"
                    rel="noopener noreferrer"
                    v-html="product.title"
                  />
                  <p
                    v-else
                    class="text-22 leading-27 text-[#4A4A4A] font-ww-bold"
                    v-html="product.title"
                  />
                  <p
                    class="text-17 leading-6 text-[#4A4A4A] font-ww-regular"
                    v-html="product.description"
                  />
                </div>
              </article>
            </div>

            <div
              class="flex flex-col gap-9 items-center px-6 py-11 w-full rounded-2xl bg-gradient-to-b from-[#EDF4F6] to-[#D3F8FB]"
            >
              <div
                v-if="closingContent"
                class="max-w-3xl text-17 leading-6 text-center text-[#4A4A4A] font-ww-regular"
                v-html="closingContent"
              />

              <div class="flex flex-col gap-4 items-center w-full max-w-2xl">
                <a
                  v-if="shopButtonHref"
                  :href="shopButtonHref"
                  class="inline-flex gap-2.5 justify-center items-center px-6 py-3 w-full text-lg tracking-wide text-white rounded-full btn-hover-lift bg-lifeboost-red font-ww-bold hover:opacity-90"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span v-html="shopButtonText" />
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M0.000273357 7.93934L10.5559 7.93934L6.5581 12.6702L8.04351 13.9294L13.9297 6.96471L8.04351 -2.57293e-07L6.5581 1.25922L10.5559 5.99008L0.000273443 5.99008L0.000273357 7.93934Z" fill="white"/>
</svg>

                </a>
                <button
                  v-else
                  type="button"
                  class="inline-flex gap-2.5 justify-center items-center px-6 py-4 w-full text-lg tracking-wide text-white rounded-full bg-lifeboost-red font-ww-bold hover:opacity-90"
                >
                  <span v-html="shopButtonText" />
                  <svg
                    class="rotate-90 shrink-0 size-3.5"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M8 3V13M8 13L4 9M8 13L12 9"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>

                <div class="w-full max-w-sm">
                  <img
                    v-if="paymentImageSrc && isPaymentSvg"
                    :src="paymentImageSrc"
                    :alt="paymentImageAlt"
                    width="404"
                    height="33"
                    loading="lazy"
                    class="object-contain mx-auto w-full h-auto"
                  />
                  <nuxt-img
                    v-else-if="paymentImageSrc"
                    :src="paymentImageSrc"
                    :alt="paymentImageAlt"
                    width="404"
                    height="33"
                    format="webp"
                    loading="lazy"
                    sizes="xs:100vw lg:404px"
                    class="object-contain mx-auto w-full h-auto"
                  />
                  <div
                    v-else
                    class="mx-auto w-full max-w-sm h-8 rounded bg-[#C5D2E1]/60"
                    aria-hidden="true"
                  />
                </div>
              </div>

              <div
                v-if="benefitTags.length"
                class="h-px w-full bg-[#C5D2E1]"
                aria-hidden="true"
              />

              <div
                v-if="benefitTags.length"
                class="flex flex-wrap gap-2 justify-center w-full"
              >
                <div
                  v-for="(tag, index) in benefitTags"
                  :key="index"
                  class="inline-flex gap-2.5 items-start px-3 py-1.5 rounded-full border border-[#93E2EA] bg-white/75"
                >
                  <span
                    class="text-sm leading-6 font-ww-regular"
                    v-html="tag.emoji"
                  />
                  <span
                    class="text-13 leading-6 text-[#4A4A4A] font-ww-regular whitespace-nowrap"
                    v-html="tag.label"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
const props = defineProps({
  brandLine: {
    type: String,
    default: 'The Lifeboost'
  },
  title: {
    type: String,
    default: "Men's Health Stack"
  },
  formulaContent: {
    type: String,
    default: `<p>Clean coffee  +  functional blends  +  mitochondrial support</p>
<p>= </p>
<p>a daily ritual that supports your entire body.</p>`
  },
  introContent: {
    type: String,
    default: `<p>Men don't need complicated routines.</p>
<p>They need simple, powerful habits they can stick to.</p>
<p>That's why Lifeboost created a lineup of products that support the core pillars of men's health:</p>`
  },
  products: {
    type: Array,
    default: () => [
      {
        imageSrc: '/assets/images/mens-health-stack-coffee.png',
        imageAlt: 'Lifeboost clean low-acid coffee',
        title: 'Clean, low-acid, toxin-tested coffee',
        href: 'https://lifeboostcoffee.com/collections/best-sellers',
        description: 'Supports heart, brain, gut, and metabolic health.'
      },
      {
        imageSrc: '/assets/images/mens-health-stack-cognition.png',
        imageAlt: 'Lifeboost Cognition Mushroom Coffee',
        title: 'Cognition Coffee',
        href: 'https://lifeboostcoffee.com/products/cognition-mushroom-coffee',
        description: "Lion's mane + chaga + clean coffee for cognitive performance."
      },
      {
        imageSrc: '/assets/images/mens-health-stack-mindflow.png',
        imageAlt: 'Lifeboost Mindflow instant functional coffee',
        title: 'Mindflow',
        href: 'https://lifeboostcoffee.com/products/mindflow-instant',
        description: 'Instant functional coffee with Alpha-GPC, coffee-fruit extract, electrolytes, collagen, and adaptogens.'
      },
      {
        imageSrc: '/assets/images/mens-health-stack-mitocreamer.png',
        imageAlt: 'Lifeboost Mitocreamer',
        title: 'Mitocreamer',
        href: 'https://lifeboostcoffee.com/products/new-mito-creamer',
        description: 'Mitochondrial support for clean, sustained energy.'
      }
    ]
  },
  closingContent: {
    type: String,
    default: `<p>Together, they form a simple, daily ritual that supports the areas men struggle with most — heart health, brain health, gut health, muscle, sexual health, and energy.</p>`
  },
  shopButtonText: {
    type: String,
    default: 'Shop Now'
  },
  shopButtonHref: {
    type: String,
    default: 'https://lifeboostcoffee.com/collections/best-sellers'
  },
  paymentImageSrc: {
    type: String,
    default: '/assets/images/mens-health-stack-payment.svg'
  },
  paymentImageAlt: {
    type: String,
    default: 'Accepted payment methods'
  },
  benefitTags: {
    type: Array,
    default: () => [
      { emoji: '❤️', label: 'Hearth Health' },
      { emoji: '💪', label: 'Muscle & Metabolism' },
      { emoji: '🧠', label: 'Brain Health' },
      { emoji: '🥒', label: 'Gut Health' },
      { emoji: '🔥', label: 'Sexual Health' },
      { emoji: '⚡', label: 'All-Day Energy' }
    ]
  },
  paddingTop: {
    type: String,
    default: 'reduced',
    validator: (value) => ['default', 'reduced'].includes(value)
  },
  paddingBottom: {
    type: String,
    default: 'reduced',
    validator: (value) => ['default', 'reduced'].includes(value)
  }
})

const isPaymentSvg = computed(
  () => !!props.paymentImageSrc && props.paymentImageSrc.endsWith('.svg')
)

defineBuilderComponent({
  friendlyName: "Content - Mens Health Stack",
  inputs: [
    {
      name: 'brandLine',
      type: 'text',
      defaultValue: 'The Lifeboost'
    },
    {
      name: 'title',
      type: 'richText',
      defaultValue: "Men's Health Stack"
    },
    {
      name: 'formulaContent',
      type: 'richText',
      defaultValue: `<p>Clean coffee  +  functional blends  +  mitochondrial support</p>
<p>= </p>
<p>a daily ritual that supports your entire body.</p>`
    },
    {
      name: 'introContent',
      type: 'richText',
      defaultValue: `<p>Men don't need complicated routines.</p>
<p>They need simple, powerful habits they can stick to.</p>
<p>That's why Lifeboost created a lineup of products that support the core pillars of men's health:</p>`
    },
    {
      name: 'products',
      type: 'list',
      defaultValue: [
        {
          imageSrc: '/assets/images/mens-health-stack-coffee.png',
          imageAlt: 'Lifeboost clean low-acid coffee',
          title: 'Clean, low-acid, toxin-tested coffee',
          href: 'https://lifeboostcoffee.com/collections/best-sellers',
          description: 'Supports heart, brain, gut, and metabolic health.'
        },
        {
          imageSrc: '/assets/images/mens-health-stack-cognition.png',
          imageAlt: 'Lifeboost Cognition Mushroom Coffee',
          title: 'Cognition Coffee',
          href: 'https://lifeboostcoffee.com/products/cognition-mushroom-coffee',
          description: "Lion's mane + chaga + clean coffee for cognitive performance."
        },
        {
          imageSrc: '/assets/images/mens-health-stack-mindflow.png',
          imageAlt: 'Lifeboost Mindflow instant functional coffee',
          title: 'Mindflow',
          href: 'https://lifeboostcoffee.com/products/mindflow-instant',
          description: 'Instant functional coffee with Alpha-GPC, coffee-fruit extract, electrolytes, collagen, and adaptogens.'
        },
        {
          imageSrc: '/assets/images/mens-health-stack-mitocreamer.png',
          imageAlt: 'Lifeboost Mitocreamer',
          title: 'Mitocreamer',
          href: 'https://lifeboostcoffee.com/products/new-mito-creamer',
          description: 'Mitochondrial support for clean, sustained energy.'
        }
      ],
      subFields: [
        {
          name: 'imageSrc',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
          defaultValue: ''
        },
        {
          name: 'imageAlt',
          type: 'text',
          defaultValue: ''
        },
        {
          name: 'title',
          type: 'text',
          defaultValue: ''
        },
        {
          name: 'href',
          type: 'text',
          defaultValue: ''
        },
        {
          name: 'description',
          type: 'longText',
          defaultValue: ''
        }
      ]
    },
    {
      name: 'closingContent',
      type: 'richText',
      defaultValue: `<p>Together, they form a simple, daily ritual that supports the areas men struggle with most — heart health, brain health, gut health, muscle, sexual health, and energy.</p>`
    },
    {
      name: 'shopButtonText',
      type: 'text',
      defaultValue: 'Shop Now'
    },
    {
      name: 'shopButtonHref',
      type: 'text',
      defaultValue: 'https://lifeboostcoffee.com/collections/best-sellers'
    },
    {
      name: 'paymentImageSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'svg'],
      defaultValue: '/assets/images/mens-health-stack-payment.svg'
    },
    {
      name: 'paymentImageAlt',
      type: 'text',
      defaultValue: 'Accepted payment methods'
    },
    {
      name: 'benefitTags',
      type: 'list',
      defaultValue: [
        { emoji: '❤️', label: 'Hearth Health' },
        { emoji: '💪', label: 'Muscle & Metabolism' },
        { emoji: '🧠', label: 'Brain Health' },
        { emoji: '🥒', label: 'Gut Health' },
        { emoji: '🔥', label: 'Sexual Health' },
        { emoji: '⚡', label: 'All-Day Energy' }
      ],
      subFields: [
        {
          name: 'emoji',
          type: 'text',
          defaultValue: ''
        },
        {
          name: 'label',
          type: 'text',
          defaultValue: ''
        }
      ]
    },
    {
      name: 'paddingTop',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'reduced'
    },
    {
      name: 'paddingBottom',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'reduced'
    }
  ]
})
</script>
